import { DateTime } from 'luxon';

// Client-side preview only — a lightweight port of the same daily/weekly/monthly
// counting logic the backend uses (Node/utils/blockSeries.js) to generate
// occurrences and enforce MAX_OCCURRENCES. Kept in sync by convention rather
// than a shared module, since Node/ and React/ don't share code today.
export const MAX_OCCURRENCES = 365;

// `frequency: 'none'` means "does not repeat" — there's no separate `enabled`
// flag, so the recurrence rule can't drift out of sync with whether it's on.
export const DEFAULT_RECURRENCE = {
  frequency: 'none',
  interval: 1,
  daysOfWeek: [],
  endType: 'after',
  endCount: 12,
  endDate: '',
};

// Returns the occurrence count for a recurrence rule, capped at MAX_OCCURRENCES + 1
// so callers can detect "exceeds the limit" vs. an exact count.
export const countOccurrences = ({ startDateTime, frequency, interval, daysOfWeek, endType, endCount, endDate }) => {
  const first = startDateTime ? DateTime.fromISO(startDateTime) : null;
  if (!first || !first.isValid || !interval || interval < 1) return 0;

  const endDateLimit = endType === 'on_date' && endDate ? DateTime.fromISO(endDate).endOf('day') : null;
  const limit = MAX_OCCURRENCES + 1;
  let count = 0;
  const withinEnd = (day) => (endType === 'after' ? count < (endCount || 0) : !!endDateLimit && day <= endDateLimit);

  if (frequency === 'daily') {
    for (let i = 0; count < limit; i++) {
      const day = first.startOf('day').plus({ days: i * interval });
      if (!withinEnd(day)) break;
      count++;
    }
  } else if (frequency === 'weekly') {
    if (!daysOfWeek || daysOfWeek.length === 0) return 0;
    const days = [...daysOfWeek].sort((a, b) => a - b);
    const firstWeekStart = first.startOf('week');
    const walkLimit = first.plus({ years: 10 });
    let cursor = first.startOf('day');
    while (cursor <= walkLimit && count < limit) {
      const dow = cursor.weekday === 7 ? 0 : cursor.weekday;
      if (days.includes(dow)) {
        const weeksSinceFirst = Math.floor(cursor.diff(firstWeekStart, 'weeks').weeks);
        if (weeksSinceFirst % interval === 0) {
          if (!withinEnd(cursor)) break;
          count++;
        }
      }
      cursor = cursor.plus({ days: 1 });
    }
  } else if (frequency === 'monthly') {
    for (let i = 0; count < limit; i++) {
      const targetMonth = first.startOf('month').plus({ months: i * interval });
      const occDay = targetMonth.set({ day: Math.min(first.day, targetMonth.daysInMonth) });
      if (!withinEnd(occDay)) break;
      count++;
    }
  }
  return count;
};
